import { useState, useRef, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { CheckCircle, X, Upload, Loader2, Image as ImageIcon } from 'lucide-react';
import type { MaterialItem } from '../types';
import { CATEGORY_CONFIG, WATER_TYPE_LABELS, inputClass, labelClass } from '../constants';
import { ModalitySelector } from './ModalitySelector';
import { apiClient } from '@/services/api-client';
import { useExtractProduct } from '../hooks/useStoreMaterials';

interface StoreMaterialEditProps {
    material: MaterialItem;
    onSave: (data: Record<string, unknown>) => void;
    onCancel: () => void;
    isSaving: boolean;
}

export function StoreMaterialEdit({ material, onSave, onCancel, isSaving }: StoreMaterialEditProps) {
    const [title, setTitle] = useState(material.title);
    const [description, setDescription] = useState(material.description || '');
    const [price, setPrice] = useState(material.price ? String(material.price) : '');
    const [category, setCategory] = useState(material.category);
    const [waterType, setWaterType] = useState(material.water_type);
    const [affiliateLink, setAffiliateLink] = useState(material.affiliate_link || '');
    const [reason, setReason] = useState(material.recommendation_reason || '');
    const [modalities, setModalities] = useState<number[]>(
        Array.isArray(material.modalities) ? (material.modalities as number[]) : []
    );
    const [images, setImages] = useState<string[]>(
        Array.isArray(material.images) ? material.images : []
    );
    const [isUploadingImages, setIsUploadingImages] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const extractMutation = useExtractProduct();
    const [isExtracting, setIsExtracting] = useState(false);
    const [extractError, setExtractError] = useState<string | null>(null);

    const handleExtract = async () => {
        const url = affiliateLink.trim();
        if (!url) return;

        setIsExtracting(true);
        setExtractError(null);
        try {
            const data = await extractMutation.mutateAsync(url);

            // Update fields only if they are empty or user wants to overwrite (implicit here by action)
            // Ideally we fill empty ones or replace specific ones. Let's fill and append images.
            if (data.title) setTitle(data.title);
            if (data.description) setDescription(data.description);
            if (data.price) setPrice(String(data.price));
            if (data.currency) { /* We stick to EUR usually, but could update if needed */ }
            if (data.images && data.images.length > 0) {
                // Append new images to existing ones unique check could be good but simple append is safer
                const validImages = data.images.filter(img => img && typeof img === 'string' && img.trim().length > 0);
                setImages(prev => [...prev, ...validImages]);
            }
            if (data.rating) {
                // Since these are in the parent 'material' object usually not editable directly in this form state unless we added inputs for them?
                // The form HAS inputs for externalRating/reviewsCount! logic is needed there only if those inputs exist.
                // Checking the form JSX below... yes, externalRating is NOT in the form state currently displayed?
                // Wait, reviewing previous `StoreMaterialEdit.tsx` content... 
                // It DOES NOT have external rating inputs in the JSX I saw earlier (lines 78-120ish).
                // It only has: Title, Description, Price, Link, Category, WaterType, Modalities, Reason, Images.
                // So I will just update those.
            }
        } catch (err: any) {
            setExtractError(err.message || 'Error al extraer datos');
        } finally {
            setIsExtracting(false);
        }
    };

    const handleImageUpload = async (files: FileList) => {
        setIsUploadingImages(true);
        try {
            const newUrls: string[] = [];
            for (const file of Array.from(files)) {
                if (!file.type.startsWith('image/')) continue;
                const ts = Date.now();
                const ext = file.name.split('.').pop() || 'jpg';
                const path = `materials/${material.id}/${ts}-${Math.random().toString(36).slice(2, 6)}.${ext}`;
                const result = await apiClient.uploadFile('material-images', path, file);
                if (result.data?.url) newUrls.push(result.data.url);
            }
            setImages(prev => [...prev, ...newUrls]);
        } finally {
            setIsUploadingImages(false);
        }
    };

    const removeImage = (idx: number) => {
        setImages(prev => prev.filter((_, i) => i !== idx));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const data: Record<string, unknown> = {
            p_title: title,
            p_description: description || null,
            p_price: price ? parseFloat(price) : null,
            p_category: category,
            p_water_type: waterType,
            p_affiliate_link: affiliateLink || null,
            p_recommendation_reason: reason || null,
            p_status: material.status,
            p_images: JSON.stringify(images),
            p_modalities: JSON.stringify(modalities),
        };
        onSave(data);
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4 p-4 rounded-lg border border-primary/20 bg-primary/5">
            <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                Editar Material
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                    <label className={labelClass}>Título</label>
                    <input type="text" value={title} onChange={e => setTitle(e.target.value)} className={inputClass} />
                </div>

                <div className="md:col-span-2">
                    <label className={labelClass}>Descripción</label>
                    <textarea
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        rows={3}
                        className={cn(inputClass, 'resize-y')}
                    />
                </div>

                <div>
                    <label className={labelClass}>Precio (€)</label>
                    <input type="number" step="0.01" value={price} onChange={e => setPrice(e.target.value)} className={inputClass} />
                </div>

                <div className="md:col-span-2">
                    <label className={labelClass}>Enlace Afiliado</label>
                    <div className="flex gap-2">
                        <input
                            type="url"
                            value={affiliateLink}
                            onChange={e => setAffiliateLink(e.target.value)}
                            className={cn(inputClass, 'flex-1')}
                            placeholder="https://..."
                        />
                        <button
                            type="button"
                            onClick={handleExtract}
                            disabled={isExtracting || !affiliateLink.trim()}
                            className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-colors disabled:opacity-50 whitespace-nowrap"
                        >
                            {isExtracting ? (
                                <><Loader2 size={12} className="animate-spin" /> Extrayendo...</>
                            ) : (
                                <><Upload size={12} className="rotate-180" /> Extraer Datos</>
                            )}
                        </button>
                    </div>
                    {extractError && (
                        <p className="text-[10px] text-amber-400 mt-1">
                            ⚠️ {extractError}
                        </p>
                    )}
                </div>

                <div>
                    <label className={labelClass}>Categoría</label>
                    <select value={category} onChange={e => setCategory(e.target.value)} className={inputClass}>
                        {Object.entries(CATEGORY_CONFIG).map(([k, v]) => (
                            <option key={k} value={k}>{v.label}</option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className={labelClass}>Tipo de Agua</label>
                    <select value={waterType} onChange={e => setWaterType(e.target.value)} className={inputClass}>
                        {Object.entries(WATER_TYPE_LABELS).map(([k, v]) => (
                            <option key={k} value={k}>{v.label}</option>
                        ))}
                    </select>
                </div>

                {/* Modalities Selector */}
                <div className="md:col-span-2">
                    <label className={labelClass}>Modalidades de Pesca</label>
                    <ModalitySelector
                        selected={modalities}
                        onChange={setModalities}
                    />
                </div>

                <div className="md:col-span-2">
                    <label className={labelClass}>Razón de Recomendación</label>
                    <textarea
                        value={reason}
                        onChange={e => setReason(e.target.value)}
                        rows={2}
                        className={cn(inputClass, 'resize-y')}
                    />
                </div>

                {/* Image Management */}
                <div className="md:col-span-2">
                    <label className={labelClass}>Imágenes ({images.length})</label>
                    <div className="flex gap-2 flex-wrap mt-1">
                        {images.map((url, i) => (
                            <div key={i} className="relative group w-20 h-20 rounded-lg overflow-hidden border border-border/50">
                                <img src={url} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                <button
                                    type="button"
                                    onClick={() => removeImage(i)}
                                    className="absolute top-0.5 right-0.5 p-0.5 rounded-full bg-red-500/80 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                    <X size={10} />
                                </button>
                            </div>
                        ))}
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isUploadingImages}
                            className="w-20 h-20 rounded-lg border-2 border-dashed border-border hover:border-primary/50 flex flex-col items-center justify-center text-muted-foreground hover:text-primary transition-colors disabled:opacity-50"
                        >
                            {isUploadingImages ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                            <span className="text-[9px] mt-0.5">{isUploadingImages ? 'Subiendo...' : 'Subir'}</span>
                        </button>
                    </div>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={e => e.target.files && handleImageUpload(e.target.files)}
                    />
                </div>
            </div>

            <div className="flex items-center gap-2 pt-2">
                <button
                    type="submit"
                    disabled={isSaving}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                    <CheckCircle size={14} />
                    Guardar Cambios
                </button>
                <button
                    type="button"
                    onClick={onCancel}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                >
                    Cancelar
                </button>
            </div>
        </form>
    );
}
